import { CloseIcon, MenuIcon } from '@wonderschool/common-base-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Image, Menu } from 'semantic-ui-react';

// Import components.
import ChangeLanguage from '../../Components/Shared/ChangeLanguage';
import { FeatureFlagsLink } from '../../feature-flags';
import { useNetworkLogo } from '../../hooks/useNetworksLogo';
import WSA from '../../WSA';
import LeftNavigation from './LeftNavigation';
import MirroredLeftNav from './MirroredLeftNav';
import { useMirroredLeftNav } from './useMirroredLeftNav';
import UserProfileMenu from './UserProfileMenu';

// White logo for the navy drawer; the top bar (white) keeps the brand logo.
import whiteLogo from '../../assets/images/svg/white-primary-logo.svg?url';

const MobileNavigation = ({ user: _user, permissions: _permissions, handleToggle: _handleToggle }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isReady, isAuthenticated } = WSA.Components.useWSAuth();
  const logoUrl = useNetworkLogo();
  const { isActive: useMirroredNav, isLoading: mirroredNavLoading } = useMirroredLeftNav();

  if (!(isReady && isAuthenticated)) return null;

  const closeMenu = () => setMenuOpen(false);

  if (useMirroredNav) {
    return <MirroredMobileNavigation logoUrl={logoUrl} />;
  }

  // Don't flash the legacy nav while the mirrored nav fetch is in flight.
  if (mirroredNavLoading) return null;

  return (
    <>
      {/* Top bar (white background): hamburger + brand logo */}
      <Menu secondary size="large">
        <Menu.Item
          className="hamburger"
          role="button"
          aria-label={t('navigation.mobile.openMenuLabel')}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon className="size-6" />
        </Menu.Item>
        <Menu.Item as="a">
          <Image size="medium" src={logoUrl} alt={t('navigation.mobile.logoAlt')} />
        </Menu.Item>
      </Menu>

      {/* Dimmed backdrop — fades in, click to dismiss */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMenu}
        aria-hidden
      />

      {/* Sliding navy drawer */}
      <aside
        className={`mobile-nav-drawer fixed inset-0 z-50 flex h-full w-full flex-col bg-navy-900 shadow-xl transition-transform duration-300 ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label={t('navigation.mobile.mainLabel')}
        aria-hidden={!menuOpen}
      >
        {/* Drawer header: white logo + close */}
        <div className="flex shrink-0 items-center justify-between py-5 pl-5 pr-4">
          <img src={whiteLogo} alt={t('navigation.mobile.logoAlt')} className="w-[160px] shrink-0" />
          <button
            type="button"
            onClick={closeMenu}
            aria-label={t('navigation.mobile.closeMenuLabel')}
            className="flex h-11 w-11 items-center justify-center rounded-md text-blue-200 hover:bg-white/10 hover:text-white"
          >
            <CloseIcon className="size-5" />
          </button>
        </div>

        {/* Scrollable nav body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Menu id="main-menu" stackable>
            <LeftNavigation onClickMenuItem={closeMenu} />
          </Menu>
        </div>

        {/* Footer controls, styled light and evenly spaced for the navy drawer */}
        <div className="mobile-nav-footer flex shrink-0 flex-col items-start gap-4 border-t border-navy-800 px-5 py-4">
          <FeatureFlagsLink mobile />
          <ChangeLanguage />
          <UserProfileMenu viewport="mobile" onClick={closeMenu} />
        </div>
      </aside>
    </>
  );
};

/**
 * Mobile navigation backed by the mirrored Nova nav. Renders the legacy app's
 * top bar (hamburger + logo) but delegates the drawer to Nova's full-screen
 * `#left-nav-mobile` overlay, which has its own header and close button.
 */
function MirroredMobileNavigation({ logoUrl }) {
  const { t } = useTranslation();
  const openMirroredNav = useCallback(() => {
    // Nova's #left-nav-mobile is inside the MirroredLeftNav container.
    // It starts hidden; we reveal it on hamburger tap.
    const container = document.getElementById('mirrored-left-nav-mobile');
    const mobileNav = container?.querySelector('#left-nav-mobile');
    if (mobileNav) {
      mobileNav.classList.remove('hidden');
      mobileNav.style.display = 'block';
    }
  }, []);

  return (
    <>
      {/* Top bar: same hamburger + logo as legacy, but opens the Nova overlay */}
      <Menu secondary size="large">
        <Menu.Item
          className="hamburger"
          role="button"
          aria-label={t('navigation.mobile.openMenuLabel')}
          onClick={openMirroredNav}
        >
          <MenuIcon className="size-6" />
        </Menu.Item>
        <Menu.Item as="a">
          <Image size="medium" src={logoUrl} alt={t('navigation.mobile.logoAlt')} />
        </Menu.Item>
      </Menu>

      {/* The full Nova HTML is injected here. The desktop sidebar portion stays
          hidden (Semantic UI's `.hidden` class keeps it so). The mobile overlay
          (#left-nav-mobile) starts hidden and is revealed by the hamburger. */}
      <MirroredLeftNav variant="mobile" />
    </>
  );
}

export default MobileNavigation;
